import { useEffect, useState } from 'react';
import {
  Sparkles, MapPin, Languages, Compass, Users, UsersRound, CalendarCheck, Heart,
} from 'lucide-react';
import api from '../lib/api';

/**
 * Q39 Phase C — CompatibilityMatch
 *
 * Renders a "Why you may get along" block on another user's profile.
 * Hidden on own profile or when score is 0. Weighted scoring + reason
 * list are computed by the backend (`GET /api/users/{id}/compatibility`).
 */
const ICON_MAP = {
  sparkles: Sparkles,
  map_pin: MapPin,
  languages: Languages,
  compass: Compass,
  users: Users,
  users_round: UsersRound,
  calendar_check: CalendarCheck,
};

const LEVEL_STYLE = {
  great_match: {
    badge: 'bg-[#B6E600] text-[#111]',
    badgeText: 'Strong match',
    border: 'border-[#B6E600]',
    headline: 'Strong vibe match',
  },
  good_match: {
    badge: 'bg-[#3F5200] text-white',
    badgeText: 'Good match',
    border: 'border-[#3F5200]/40',
    headline: 'Likely to get along',
  },
  some_overlap: {
    badge: 'bg-[#F1F0EA] text-[#3F5200] border border-[#3F5200]/30',
    badgeText: 'Some overlap',
    border: 'border-black/[0.08]',
    headline: 'Some shared ground',
  },
  few_signals: {
    badge: 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.08]',
    badgeText: 'Few signals',
    border: 'border-black/[0.08]',
    headline: 'Not much overlap yet',
  },
};

export default function CompatibilityMatch({ targetUserId, isOwn, displayName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOwn || !targetUserId) return;
    let alive = true;
    setLoading(true);
    api.get(`/users/${targetUserId}/compatibility`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [targetUserId, isOwn]);

  if (isOwn) return null;
  if (loading) {
    return (
      <div
        data-testid="compat-match-loading"
        className="bg-white rounded-2xl border border-black/[0.06] p-4 mt-3 animate-pulse"
      >
        <div className="h-3 w-32 bg-black/[0.08] rounded mb-3" />
        <div className="h-3 w-full bg-black/[0.04] rounded mb-2" />
        <div className="h-3 w-3/4 bg-black/[0.04] rounded" />
      </div>
    );
  }
  if (!data || !data.available) return null;

  const reasons = Array.isArray(data.reasons) ? data.reasons : [];
  // If no signal at all, hide
  if (reasons.length === 0) return null;

  const level = LEVEL_STYLE[data.level] || LEVEL_STYLE.few_signals;
  const firstName = (displayName || '').split(' ')[0] || 'them';

  return (
    <section
      data-testid="compat-match"
      data-level={data.level}
      className={`bg-white rounded-2xl border ${level.border} p-4 mt-3`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-[#B6E600]/15 flex items-center justify-center">
            <Heart size={15} className="text-[#3F5200]" strokeWidth={2.6} fill="#B6E600" />
          </span>
          <h3 className="text-sm font-extrabold text-[#111]">Why you may get along</h3>
        </div>
        <span
          data-testid="compat-match-level"
          className={`text-[10px] font-extrabold px-2 py-1 rounded-full uppercase tracking-wider ${level.badge}`}
        >
          {level.badgeText}
        </span>
      </div>

      <p
        data-testid="compat-match-headline"
        className="text-[12.5px] font-bold text-[#3F5200] mb-3 leading-snug"
      >
        {data.headline || level.headline} with {firstName}
      </p>

      <ul data-testid="compat-match-reasons" className="space-y-2">
        {reasons.map((r) => {
          const Icon = ICON_MAP[r.icon] || Sparkles;
          return (
            <li
              key={`${r.key}-${r.label}`}
              data-testid={`compat-reason-${r.key}`}
              className="flex items-start gap-2.5 text-[13px] text-[#111] leading-snug"
            >
              <span className="w-5 h-5 rounded-full bg-[#3F5200]/10 flex items-center justify-center shrink-0 mt-0.5">
                <Icon size={11} className="text-[#3F5200]" strokeWidth={2.8} />
              </span>
              <span className="flex-1">{r.label}</span>
            </li>
          );
        })}
      </ul>

      <p className="text-[10.5px] text-[#9B9EA3] mt-3 leading-relaxed">
        Based on shared interests, mutual connections, plans you both attended, and what you each shared on your profile.
      </p>
    </section>
  );
}
